import { useTheme } from "next-themes";
import { Toaster as Sonner } from "sonner";

type ToasterProps = React.ComponentProps<typeof Sonner>;

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      richColors={false}
      closeButton={true}
      position="top-right"
      duration={4000}
      className="fixed top-0 right-0 z-50 pointer-events-none"
      toastOptions={{
        classNames: {
          // Base toast styling - Clean and minimal like in the image
          toast: `
            pointer-events-auto transition-all duration-300 ease-in-out transform-gpu opacity-100 
            w-fit min-w-80 max-w-md rounded-lg shadow-lg p-4 min-h-14 flex items-start gap-3
            data-[state=open]:translate-y-0 data-[state=open]:opacity-100
            data-[state=closed]:-translate-y-2 data-[state=closed]:opacity-0
            group hover:[&_.sonner-toast-close]:opacity-100 ring-0
            max-[480px]:min-w-[calc(100vw-2rem)] max-[480px]:max-w-[calc(100vw-2rem)] max-[480px]:mx-4
            
            // Light mode (white background, dark text)
            bg-white text-gray-900 border border-gray-200 
            
            // Dark mode (dark background, light text)  
            dark:bg-gray-900 dark:text-gray-100 dark:border-gray-800
          `,

          // Title styling - Bold and clean with proper contrast
          title: `
            !font-semibold !text-sm !leading-5 !m-0
            text-gray-900 dark:text-gray-100
            [data-theme="light"]_&:text-gray-900 [data-theme="dark"]_&:text-gray-100
          `,

          // Description styling - Subtle but visible
          description: `
            !font-normal !text-sm !leading-5 !mt-1
            text-gray-600 dark:text-gray-300
            [data-theme="light"]_&:text-gray-600 [data-theme="dark"]_&:text-gray-300
          `,

          // Icon styling - Small and visible with proper contrast
          icon: `
            !w-4 !h-4 !flex-shrink-0 !mr-3
            text-gray-700 dark:text-gray-200
          `,

          // Close button - Minimal but visible
          closeButton: `
            sonner-toast-close !absolute !top-2 !right-2 !w-4 !h-4 !border-0 !bg-transparent
            !cursor-pointer !rounded !flex !items-center !justify-center !opacity-0 
            !transition-opacity !duration-200 hover:!opacity-100
            text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300
          `,

          // Success variant - Green border like in image
          success: `
            !border-l-4 !border-l-green-500
            !bg-white dark:!bg-gray-900
            [&_svg]:!text-green-500 [&_.lucide]:!text-green-500
            [&_[data-title]]:!text-gray-900 dark:[&_[data-title]]:!text-gray-100
            [&_[data-description]]:!text-gray-600 dark:[&_[data-description]]:!text-gray-300
          `,

          // Error variant - Red border like in image
          error: `
            !border-l-4 !border-l-red-500
            !bg-white dark:!bg-gray-900  
            [&_svg]:!text-red-500 [&_.lucide]:!text-red-500
            [&_[data-title]]:!text-gray-900 dark:[&_[data-title]]:!text-gray-100
            [&_[data-description]]:!text-gray-600 dark:[&_[data-description]]:!text-gray-300
          `,

          // Warning variant - Orange/Yellow border like in image
          warning: `
            !border-l-4 !border-l-orange-500
            !bg-white dark:!bg-gray-900
            [&_svg]:!text-orange-500 [&_.lucide]:!text-orange-500
            [&_[data-title]]:!text-gray-900 dark:[&_[data-title]]:!text-gray-100
            [&_[data-description]]:!text-gray-600 dark:[&_[data-description]]:!text-gray-300
          `,

          // Info variant - Blue border like in image
          info: `
            !border-l-4 !border-l-blue-500
            !bg-white dark:!bg-gray-900
            [&_svg]:!text-blue-500 [&_.lucide]:!text-blue-500
            [&_[data-title]]:!text-gray-900 dark:[&_[data-title]]:!text-gray-100
            [&_[data-description]]:!text-gray-600 dark:[&_[data-description]]:!text-gray-300
          `,

          // Loading variant - Blue border with spinning icon
          loading: `
            !border-l-4 !border-l-blue-500
            !bg-white dark:!bg-gray-900
            [&_svg]:!text-blue-500 [&_.lucide]:!text-blue-500 [&_svg]:!animate-spin [&_.lucide]:!animate-spin
            [&_[data-title]]:!text-gray-900 dark:[&_[data-title]]:!text-gray-100
            [&_[data-description]]:!text-gray-600 dark:[&_[data-description]]:!text-gray-300
          `,

          // Action button - Minimal style
          actionButton: `
            !px-3 !py-1.5 !text-xs !font-medium !rounded !transition-all !duration-200
            !bg-gray-100 !text-gray-900 hover:!bg-gray-200
            dark:!bg-gray-800 dark:!text-gray-100 dark:hover:!bg-gray-700
          `,

          // Cancel button - Very subtle
          cancelButton: `
            !px-3 !py-1.5 !text-xs !font-medium !rounded !transition-colors !duration-200
            !bg-transparent !text-gray-500 hover:!text-gray-700 hover:!bg-gray-50
            dark:!text-gray-400 dark:hover:!text-gray-200 dark:hover:!bg-gray-800
          `,
        },
        style: {
          fontSize: "14px",
          lineHeight: "1.4",
        },
        unstyled: false,
      }}
      {...props}
    />
  );
};

export { Toaster };
